import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  ArrowLeft,
  Dog,
  Camera,
  Calendar,
  FileText,
  Image,
  Upload,
  BookOpen,
  GraduationCap,
  Download,
  CheckCircle,
  Edit
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PuppyProfilePage = () => {
  const { puppyId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const [puppy, setPuppy] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [progressReports, setProgressReports] = useState([]);
  const [homework, setHomework] = useState([]);
  const [graduation, setGraduation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    breed: "",
    date_of_birth: "",
    pickup_address: "",
    temperament_notes: "",
    vaccination_status: "unknown"
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [puppyRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/puppies/${puppyId}`, { headers }),
        axios.get(`${API}/enrollments`, { headers })
      ]);
      setPuppy(puppyRes.data);
      
      // Filter enrollments for this puppy
      const puppyEnrollments = enrollmentsRes.data.filter(e => e.puppy_id === puppyId);
      setEnrollments(puppyEnrollments);
      
      // Get progress reports and homework for the first enrollment
      if (puppyEnrollments.length > 0) {
        const activeEnrollment = puppyEnrollments.find(e => 
          ["confirmed", "in_progress", "completed"].includes(e.status)
        ) || puppyEnrollments[0];
        setSelectedEnrollment(activeEnrollment);
        
        const [reportsRes, homeworkRes] = await Promise.all([
          axios.get(`${API}/progress-reports/enrollment/${activeEnrollment.id}`, { headers }),
          axios.get(`${API}/homework/enrollment/${activeEnrollment.id}`, { headers })
        ]);
        setProgressReports(reportsRes.data);
        setHomework(homeworkRes.data);
        
        // Check for graduation
        if (activeEnrollment.status === "completed") {
          try {
            const gradRes = await axios.get(`${API}/graduations/enrollment/${activeEnrollment.id}`, { headers });
            setGraduation(gradRes.data);
          } catch (e) {
            // No graduation record exists yet - this is expected for non-graduated puppies
            setGraduation(null);
          }
        }
      }
    } catch (err) {
      toast.error("Failed to load puppy profile");
    } finally {
      setLoading(false);
    }
  }, [puppyId, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openEditDialog = () => {
    setEditForm({
      name: puppy.name || "",
      breed: puppy.breed || "",
      date_of_birth: puppy.date_of_birth || "",
      pickup_address: puppy.pickup_address || "",
      temperament_notes: puppy.temperament_notes || "",
      vaccination_status: puppy.vaccination_status || "unknown"
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const response = await axios.put(
        `${API}/puppies/${puppyId}`,
        editForm,
        { headers }
      );
      setPuppy(response.data);
      setEditDialogOpen(false);
      toast.success("Puppy details updated successfully!");
    } catch (error) {
      toast.error("Failed to update puppy details");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/puppies/${puppyId}/photos`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo uploaded successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const markHomeworkRead = async (homeworkId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/homework/${homeworkId}/read`, {}, { headers });
      setHomework(prev => prev.map(h => 
        h.id === homeworkId ? { ...h, is_read: true } : h
      ));
    } catch (err) {
      // Silent fail - non-critical operation
      console.debug('Non-critical error marking homework as read:', err?.message);
    }
  };

  const getProgressColor = (value) => {
    if (value >= 4) return "bg-olive";
    if (value >= 3) return "bg-olive/70";
    if (value >= 2) return "bg-terracotta";
    return "bg-stone";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="animate-pulse text-olive font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  if (!puppy) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-charcoal mb-4">Puppy Not Found</h2>
          <Link to="/dashboard">
            <Button className="btn-primary">Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-off-white" data-testid="puppy-profile-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center text-stone hover:text-olive transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <Link to="/" className="flex flex-col items-end">
            <span className="font-heading text-xl italic text-charcoal">The Pack Standard</span>
            <span className="text-olive text-xs tracking-wider">PUPPY ACADEMY</span>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Puppy Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium mb-8"
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-32 h-32 rounded-full bg-olive/10 flex items-center justify-center shrink-0 mx-auto md:mx-0">
              {puppy.photos?.[0] ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${puppy.photos[0]}`}
                  alt={puppy.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <Dog className="w-16 h-16 text-olive" />
              )}
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
                <h1 className="font-heading text-4xl text-charcoal">{puppy.name}</h1>
                <Badge className={
                  puppy.vaccination_status === "up_to_date" 
                    ? "bg-olive" 
                    : puppy.vaccination_status === "partial"
                      ? "bg-terracotta"
                      : "bg-sand text-charcoal"
                }>
                  Vaccinations: {puppy.vaccination_status.replace(/_/g, " ")}
                </Badge>
                <Button 
                  onClick={openEditDialog}
                  className="btn-outline ml-auto"
                  data-testid="edit-puppy-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Details
                </Button>
              </div>
              <p className="text-stone text-lg mb-4">{puppy.breed}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-sand/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-heading text-olive">{puppy.age_weeks}</p>
                  <p className="text-sm text-stone">Weeks Old</p>
                </div>
                <div className="bg-sand/50 rounded-lg p-3 text-center col-span-2 md:col-span-3">
                  <p className="text-sm font-medium text-olive truncate">{puppy.pickup_address}</p>
                  <p className="text-sm text-stone">Pickup Address</p>
                </div>
              </div>

              {puppy.temperament_notes && (
                <div className="mt-4 p-4 bg-sand/50 rounded-lg">
                  <p className="text-sm text-stone">
                    <strong className="text-charcoal">Temperament:</strong> {puppy.temperament_notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Graduation Banner */}
        {graduation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-olive text-white rounded-2xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <GraduationCap className="w-12 h-12" />
              <div>
                <h2 className="font-heading text-2xl">Congratulations!</h2>
                <p className="text-white/80">{puppy.name} has graduated from {graduation.program_name}!</p>
              </div>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${graduation.certificate_url}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-white text-olive hover:bg-white/90" data-testid="download-certificate">
                <Download className="w-4 h-4 mr-2" />
                View Certificate
              </Button>
            </a>
          </motion.div>
        )}

        {/* Tabs for different content */}
        <Tabs defaultValue="progress" className="space-y-6">
          <TabsList className="bg-sand">
            <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
            <TabsTrigger value="homework" data-testid="tab-homework">Homework</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
          </TabsList>

          {/* Progress Tab */}
          <TabsContent value="progress">
            {progressReports.length === 0 ? (
              <div className="card-premium text-center py-12">
                <FileText className="w-16 h-16 text-olive/50 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Progress Reports Yet</h3>
                <p className="text-stone">
                  Progress reports will appear here after each training session.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {progressReports.map((report) => (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-premium"
                    data-testid={`report-${report.id}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="font-heading text-xl text-charcoal">Week {report.session_number}</h3>
                        <p className="text-stone text-sm">
                          {new Date(report.session_date).toLocaleDateString("en-AU", {
                            weekday: "long",
                            day: "numeric",
                            month: "long"
                          })}
                        </p>
                      </div>
                      <Badge className="bg-olive">Session {report.session_number}/4</Badge>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      {[
                        { label: "Recall Development", value: report.recall_development },
                        { label: "Lead Walking", value: report.lead_walking },
                        { label: "Socialisation", value: report.socialisation },
                        { label: "Confidence Level", value: report.confidence_level },
                        { label: "Impulse Control", value: report.impulse_control },
                        { label: "Calmness & Settling", value: report.calmness_settling }
                      ].map((skill) => (
                        <div key={skill.label}>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm text-charcoal">{skill.label}</span>
                            <span className="text-sm font-medium text-olive">{skill.value}/5</span>
                          </div>
                          <div className="h-2 bg-sand rounded-full overflow-hidden">
                            <div
                              className={`h-full ${getProgressColor(skill.value)} transition-all`}
                              style={{ width: `${(skill.value / 5) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-4 border-t border-olive/10 pt-6">
                      <div>
                        <h4 className="font-medium text-charcoal mb-2">Trainer Notes</h4>
                        <p className="text-stone">{report.notes}</p>
                      </div>
                      {report.highlights && (
                        <div>
                          <h4 className="font-medium text-charcoal mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-olive" />
                            Highlights
                          </h4>
                          <p className="text-stone">{report.highlights}</p>
                        </div>
                      )}
                      {report.areas_to_work_on && (
                        <div>
                          <h4 className="font-medium text-charcoal mb-2">Areas to Work On</h4>
                          <p className="text-stone">{report.areas_to_work_on}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Homework Tab */}
          <TabsContent value="homework">
            {homework.length === 0 ? (
              <div className="card-premium text-center py-12">
                <BookOpen className="w-16 h-16 text-olive/50 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="font-heading text-xl text-charcoal mb-2">No Homework Yet</h3>
                <p className="text-stone">
                  Homework assignments will appear here each week.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {homework.map((hw) => (
                  <motion.div
                    key={hw.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`card-premium ${!hw.is_read ? "border-l-4 border-l-olive" : ""}`}
                    onClick={() => !hw.is_read && markHomeworkRead(hw.id)}
                    data-testid={`homework-${hw.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Badge className="bg-sand text-charcoal mb-2">Week {hw.week_number}</Badge>
                        <h3 className="font-heading text-xl text-charcoal">{hw.title}</h3>
                      </div>
                      {!hw.is_read && (
                        <Badge className="bg-terracotta">New</Badge>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-charcoal mb-2">Exercises</h4>
                        <ul className="space-y-2">
                          {hw.exercises.map((exercise, i) => (
                            <li key={`${hw.id}-exercise-${i}`} className="flex items-start gap-2 text-stone">
                              <span className="w-1.5 h-1.5 bg-olive rounded-full mt-2 shrink-0" />
                              {exercise}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {hw.tips && (
                        <div className="p-4 bg-sand/50 rounded-lg">
                          <h4 className="font-medium text-charcoal mb-1">Tips</h4>
                          <p className="text-stone text-sm">{hw.tips}</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl text-charcoal">Photo Gallery</h2>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploading}
                  data-testid="photo-upload-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="btn-outline py-2 px-4"
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Upload Photo"}
                </Button>
              </div>
            </div>

            {puppy.photos?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {puppy.photos.map((photo, index) => (
                  <Dialog key={`photo-dialog-${photo}`}>
                    <DialogTrigger asChild>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="aspect-square rounded-xl overflow-hidden cursor-pointer"
                        data-testid={`photo-${index}`}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
                          alt={`${puppy.name} photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
                        alt={`${puppy.name} photo ${index + 1}`}
                        className="w-full h-auto rounded-lg"
                      />
                    </DialogContent>
                  </Dialog>
                ))}
              </div>
            ) : (
              <div className="card-premium text-center py-12">
                <Image className="w-16 h-16 text-olive/50 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-stone">No photos yet</p>
                <p className="text-sm text-stone mt-1">
                  Photos from training sessions will appear here.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Enroll CTA */}
        {enrollments.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 p-8 bg-sand rounded-2xl text-center"
          >
            <h3 className="font-heading text-2xl text-charcoal mb-2">
              Ready to Start {puppy.name}'s Training?
            </h3>
            <p className="text-stone mb-6">
              Enroll {puppy.name} in one of our structured puppy programs.
            </p>
            <Link to="/programs">
              <Button className="btn-primary" data-testid="enroll-puppy-btn">
                View Programs
              </Button>
            </Link>
          </motion.div>
        )}
      </main>

      {/* Edit Puppy Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit Puppy Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-breed">Breed</Label>
                <Input
                  id="edit-breed"
                  value={editForm.breed}
                  onChange={(e) => setEditForm({ ...editForm, breed: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-dob">Date of Birth</Label>
                <Input
                  id="edit-dob"
                  type="date"
                  value={editForm.date_of_birth}
                  onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-vaccination">Vaccination Status</Label>
                <Select 
                  value={editForm.vaccination_status} 
                  onValueChange={(value) => setEditForm({ ...editForm, vaccination_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">Unknown</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="up_to_date">Up to Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-address">Pickup Address</Label>
              <Input
                id="edit-address"
                value={editForm.pickup_address}
                onChange={(e) => setEditForm({ ...editForm, pickup_address: e.target.value })}
                placeholder="Enter pickup address for academy days"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-temperament">Temperament Notes</Label>
              <Textarea
                id="edit-temperament"
                value={editForm.temperament_notes}
                onChange={(e) => setEditForm({ ...editForm, temperament_notes: e.target.value })}
                placeholder="Any notes about your puppy's temperament..."
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PuppyProfilePage;
